import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
// import { ThemeToggle } from '../components/ui/theme-toggle';
import { useNoshStore } from '../lib/store';
import { useLeftEdgeSwipeDisable } from '../hooks/useLeftEdgeSwipeDisable';
import NutritionAnalysisModal from '../components/NutritionAnalysisModal';
import { MealGenerationModal, MealGenerationOptions } from '../components/ui/meal-generation-modal';
import { Trash2, Calendar, Timer, ChefHat, Sparkles, ScanLine, Search, X, Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactDOM from 'react-dom';
import { getImageDisplayUrl, cleanupOrphanedImages } from '../lib/api';
import { supabase } from '../lib/supabase';
import { useGlobalImageScanner } from '../lib/global-image-scan';
// UsageLimitModal now handled globally in App.tsx
import { CachedImage } from '../components/ui/CachedImage';
import { cn } from '../lib/utils';
import { DatabaseService, DailyNutritionSummary } from '../lib/database';
import { calculateNutritionTargets, type DietaryPreference } from '../lib/nutrition-calculator';
import { getOptimizedImageUrl } from '../lib/image-optimization';

// Helper functions for date handling
const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust for Monday as first day of week (0=Sunday, 1=Monday, etc.)
  // If Sunday (0), go back 6 days to get Monday
  // If Monday (1), go back 0 days
  // If Tuesday (2), go back 1 day, etc.
  const diff = day === 0 ? -6 : -(day - 1);
  const mondayStart = new Date(d);
  mondayStart.setDate(d.getDate() + diff);
  return mondayStart;
};

const getWeekDays = (weekStart: Date): Date[] => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    days.push(day);
  }
  return days;
};

// Helper function to get local date string in YYYY-MM-DD format
// This ensures calendar and nutrition tracking use user's local date, not UTC date
const getLocalDateString = (date?: Date): string => {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const isDateInRange = (date: Date, range: string, customStart?: string, customEnd?: string): boolean => {
  const mealDate = new Date(date);
  const today = new Date();
  
  // Use timezone-aware date comparison by creating local dates
  const mealLocalDate = new Date(mealDate.getFullYear(), mealDate.getMonth(), mealDate.getDate());
  const todayLocalDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  switch (range) {
    case 'today':
      return mealLocalDate.getTime() === todayLocalDate.getTime();
    case 'yesterday':
      const yesterdayLocal = new Date(todayLocalDate);
      yesterdayLocal.setDate(todayLocalDate.getDate() - 1);
      return mealLocalDate.getTime() === yesterdayLocal.getTime();
    case 'last7days':
      const sevenDaysAgoLocal = new Date(todayLocalDate);
      sevenDaysAgoLocal.setDate(todayLocalDate.getDate() - 7);
      return mealLocalDate >= sevenDaysAgoLocal && mealLocalDate <= todayLocalDate;
    case 'last30days':
      const thirtyDaysAgoLocal = new Date(todayLocalDate);
      thirtyDaysAgoLocal.setDate(todayLocalDate.getDate() - 30);
      return mealLocalDate >= thirtyDaysAgoLocal && mealLocalDate <= todayLocalDate;
    case 'custom':
      if (!customStart || !customEnd) return true;
      // Parse custom dates as local dates
      const startDateParts = customStart.split('-').map(Number);
      const endDateParts = customEnd.split('-').map(Number);
      const startDateLocal = new Date(startDateParts[0], startDateParts[1] - 1, startDateParts[2]);
      const endDateLocal = new Date(endDateParts[0], endDateParts[1] - 1, endDateParts[2]);
      return mealLocalDate >= startDateLocal && mealLocalDate <= endDateLocal;
    case 'all':
    default:
      return true;
  }
};

// Smart Cached Meal Image Component - user uploads use signed URLs, AI images use optimization
const CachedMealImage = ({ imagePathOrUrl, alt, className }: { 
  imagePathOrUrl: string; 
  alt: string; 
  className: string; 
}) => {
  const [finalUrl, setFinalUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadImageUrl = async () => {
      try {
        setIsLoading(true);
        
        // Check if imagePathOrUrl is empty or null
        if (!imagePathOrUrl || imagePathOrUrl.trim() === '') {
          setFinalUrl('/food.jpg'); // Use fallback for empty paths
          return;
        }
        
        // Check if this is a user-uploaded image (images bucket) or AI-generated (recipe-images bucket)
        const isUserUpload = imagePathOrUrl.includes('/images/') || 
                           (!imagePathOrUrl.includes('http') && !imagePathOrUrl.includes('recipe-images'));
        
        if (isUserUpload) {
          // User-uploaded images: Use signed URLs (they work reliably)
          const signedUrl = await getImageDisplayUrl(imagePathOrUrl, 86400); // 24 hour expiry
          setFinalUrl(signedUrl);
        } else {
          // AI-generated images: Use optimization
          const optimized = getOptimizedImageUrl(imagePathOrUrl, 'meal-tracker', {
            fallbackUrl: '/food.jpg',
            quality: 90
          });
          setFinalUrl(optimized);
        }
      } catch (error) {
        console.warn('⚠️ [MealTracker] Failed to load image:', error);
        setFinalUrl('/food.jpg'); // Fallback
      } finally {
        setIsLoading(false);
      }
    };

    loadImageUrl();
  }, [imagePathOrUrl]);

  if (isLoading) {
    return (
      <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  // Use CachedImage for the actual image display with caching
  return (
    <CachedImage
      src={finalUrl}
      alt={alt}
      className={`${className} transition-opacity duration-300`}
      loading="lazy"
      fallbackSrc="/food.jpg"
    />
  );
};

const MealTracker = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [showNutritionModal, setShowNutritionModal] = useState(false);

  const [showRecipeModal, setShowRecipeModal] = useState(false);
  
  // Usage limit modal now handled globally - removed individual state
  
  // Load More Meals state
  const [visibleMealsCount, setVisibleMealsCount] = useState(() => 
    window.innerWidth < 768 ? 6 : 9 // Mobile: 6, Desktop: 9
  );
  
  // Date filtering and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState<'today' | 'yesterday' | 'last7days' | 'last30days' | 'all' | 'custom'>('all');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getWeekStart(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  // Nutrition data for calendar progress bars
  const [dailyNutritionData, setDailyNutritionData] = useState<Map<string, DailyNutritionSummary>>(new Map());
  const [nutritionTargets, setNutritionTargets] = useState<any>(null); // null means no date selected
  const [nutritionDataLoaded, setNutritionDataLoaded] = useState(false);
  const [shouldAnimateOnce, setShouldAnimateOnce] = useState(true);
  
  // Add scroll state for frosted glass effect
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const lastScrollTop = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | null>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Mobile swipeable calendar state
  const [currentThreeDayStart, setCurrentThreeDayStart] = useState<Date>(() => {
    const today = new Date();
    today.setDate(today.getDate() - 2); // Start 2 days ago to show past 3 days
    return today;
  });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Tooltip state for AI button
  const [showGenerateTooltip, setShowGenerateTooltip] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const generateButtonRef = useRef<HTMLButtonElement>(null);
  
  // Meal scan functionality - same as Dashboard
  const mealScanInputRef = useRef<HTMLInputElement>(null);
  const mobileMealCameraRef = useRef<HTMLInputElement>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  
  // Use global image scanner
  const { isScanning, startScan } = useGlobalImageScanner();
  
  const { meals, loadMeals, deleteMeal, getMealById, inventory, showNutritionAnalysis, getCompleteMealGenerationContext, user } = useNoshStore();
  const databaseService = new DatabaseService();

  // Filter meals based on search query and date range
  const filteredMeals = meals.filter(meal => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      meal.mealName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      meal.ingredients.some(ingredient => 
        ingredient.toLowerCase().includes(searchQuery.toLowerCase())
      );
    
    // Date filter
    const mealDate = new Date(meal.createdAt);
    const matchesDate = isDateInRange(mealDate, selectedDateRange, customStartDate, customEndDate);
    
    return matchesSearch && matchesDate;
  });

  // Load More Meals functionality
  const MOBILE_INITIAL_MEALS = 6;
  const DESKTOP_INITIAL_MEALS = 9;
  const LOAD_MORE_COUNT = 6; // Same for both mobile and desktop
  
  const loadMoreMeals = () => {
    setVisibleMealsCount(prev => prev + LOAD_MORE_COUNT);
  };
  
  const hasMoreMeals = visibleMealsCount < filteredMeals.length;
  const remainingMealsCount = Math.max(0, filteredMeals.length - visibleMealsCount);

  // Reset visible meals count when filters change or mobile/desktop switch
  useEffect(() => {
    const initialCount = isMobile ? MOBILE_INITIAL_MEALS : DESKTOP_INITIAL_MEALS;
    setVisibleMealsCount(initialCount);
  }, [searchQuery, selectedDateRange, customStartDate, customEndDate, isMobile]);

  // Clear search function
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Week navigation functions
  const goToPreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  // Mobile 3-day calendar navigation functions
  const goToPrevious3Days = () => {
    const newStart = new Date(currentThreeDayStart);
    newStart.setDate(newStart.getDate() - 3);
    setCurrentThreeDayStart(newStart);
  };

  const goToNext3Days = () => {
    const newStart = new Date(currentThreeDayStart);
    newStart.setDate(newStart.getDate() + 3);
    setCurrentThreeDayStart(newStart);
  };

  const goToToday = () => {
    const today = new Date();
    
    // For mobile 3-day view: set to show past 2 days + today
    const mobile3DayStart = new Date(today);
    mobile3DayStart.setDate(today.getDate() - 2);
    setCurrentThreeDayStart(mobile3DayStart);
    
    // For desktop week view: set to show the week containing today
    const weekStart = getWeekStart(today);
    setCurrentWeekStart(weekStart);
    
    // Don't select today's date - just jump to today's week without highlighting
    setSelectedDate(null);
    setSelectedDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  // Get 3 days for mobile calendar
  const getThreeDays = (startDate: Date): Date[] => {
    const days = [];
    for (let i = 0; i < 3; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Mobile touch handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext3Days(); // Swipe left to see next 3 days
    }
    if (isRightSwipe) {
      goToPrevious3Days(); // Swipe right to see previous 3 days
    }
  };

  const handleDateClick = (date: Date) => {
    // Toggle functionality: if same date is clicked, deselect it
    if (selectedDate && selectedDate.toDateString() === date.toDateString()) {
      setSelectedDate(null);
      setSelectedDateRange('all');
      setCustomStartDate('');
      setCustomEndDate('');
    } else {
      setSelectedDate(date);
      setSelectedDateRange('custom');
      // Use timezone-aware local date instead of UTC date
      const dateStr = getLocalDateString(date);
      setCustomStartDate(dateStr);
      setCustomEndDate(dateStr);
    }
  };

  // Get date range label for UI
  const getDateRangeLabel = () => {
    switch (selectedDateRange) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'last7days':
        return 'Last 7 Days';
      case 'last30days':
        return 'Last 30 Days';
      case 'custom':
        if (customStartDate === customEndDate) {
          return formatDateForDisplay(new Date(customStartDate));
        }
        return `${customStartDate} - ${customEndDate}`;
      case 'all':
      default:
        return 'All Time';
    }
  };

  // Load nutrition data for calendar progress bars
  const loadNutritionData = async () => {
    try {
      // Don't set to false if already loaded to prevent flashing
      if (!nutritionDataLoaded) {
        setNutritionDataLoaded(false);
      }
      
      // Calculate nutrition targets if user has body stats
      if (user.age && user.gender && user.height && user.weight && user.activityLevel) {
        const targets = calculateNutritionTargets({
          age: user.age,
          gender: user.gender,
          height: user.height,
          weight: user.weight,
          activityLevel: user.activityLevel,
          goal: user.goal || 'maintain',
          dietaryPreferences: (user.preferences || []) as DietaryPreference[]
        });
        setNutritionTargets(targets);
      }

      // Load nutrition data for both desktop (weekly) and mobile (3-day) views
      const nutritionMap = new Map<string, DailyNutritionSummary>();
      
      // Get dates for current view
      const daysToLoad = isMobile ? getThreeDays(currentThreeDayStart) : getWeekDays(currentWeekStart);
      
      for (const date of daysToLoad) {
        try {
          // Use timezone-aware local date instead of UTC date
          const dateStr = getLocalDateString(date);
          const summary = await databaseService.getDailyNutritionSummary(dateStr);
          if (summary) {
            nutritionMap.set(dateStr, summary);
          }
        } catch (error) {
          console.error(`Error loading nutrition for ${getLocalDateString(date)}:`, error);
        }
      }
      
      setDailyNutritionData(nutritionMap);
      setNutritionDataLoaded(true);
      
      // Disable animation after a short delay to allow the animation to trigger first
      if (shouldAnimateOnce) {
        setTimeout(() => {
          setShouldAnimateOnce(false);
        }, 500); // Give enough time for animation to complete
      }
    } catch (error) {
      console.error('Error loading nutrition data:', error);
      setNutritionDataLoaded(true); // Set to true even on error to prevent infinite loading
    }
  };

  // Get nutrition progress for a specific date
  const getNutritionProgress = (date: Date) => {
    // Use timezone-aware local date instead of UTC date
    const dateStr = getLocalDateString(date);
    const summary = dailyNutritionData.get(dateStr);
    
    // Always return progress data, even if no nutrition data or targets
    // This ensures progress bars always show (even if empty)
    if (!nutritionTargets) {
      return {
        calories: { current: 0, target: 2000, progress: 0 }, // Default targets when no user data
        protein: { current: 0, target: 150, progress: 0 },
        carbs: { current: 0, target: 250, progress: 0 },
        fat: { current: 0, target: 67, progress: 0 },
        fiber: { current: 0, target: 25, progress: 0 }
      };
    }

    // Use actual summary data if available, otherwise use 0 values
    const currentCalories = summary?.totalCalories || 0;
    const currentProtein = summary?.totalProtein || 0;
    const currentCarbs = summary?.totalCarbs || 0;
    const currentFat = summary?.totalFat || 0;
    const currentFiber = summary?.totalFiber || 0;

    // Calculate progress percentages (same logic as DailyNutritionProgress component)
    const calorieProgress = Math.min((currentCalories / nutritionTargets.dailyCalories) * 100, 100);
    const proteinProgress = Math.min((currentProtein / nutritionTargets.macroTargets.protein.target) * 100, 100);
    const carbProgress = Math.min((currentCarbs / nutritionTargets.macroTargets.carbs.target) * 100, 100);
    const fatProgress = Math.min((currentFat / nutritionTargets.macroTargets.fat.target) * 100, 100);
    const fiberProgress = Math.min((currentFiber / nutritionTargets.macroTargets.fiber.target) * 100, 100);

    return {
      calories: { current: currentCalories, target: nutritionTargets.dailyCalories, progress: calorieProgress },
      protein: { current: currentProtein, target: nutritionTargets.macroTargets.protein.target, progress: proteinProgress },
      carbs: { current: currentCarbs, target: nutritionTargets.macroTargets.carbs.target, progress: carbProgress },
      fat: { current: currentFat, target: nutritionTargets.macroTargets.fat.target, progress: fatProgress },
      fiber: { current: currentFiber, target: nutritionTargets.macroTargets.fiber.target, progress: fiberProgress }
    };
  };

  // Compact nutrition progress bars for calendar dates
  const NutritionProgressBars = ({ date, isMobile = false }: { date: Date; isMobile?: boolean }) => {
    const progress = getNutritionProgress(date);
    
    // Progress is now always available (even if 0% for empty days)
    const barHeight = isMobile ? 'h-1.5' : 'h-1.5';
    const spacing = isMobile ? 'space-y-[2px]' : 'space-y-[1px]';
    const textSize = isMobile ? 'text-[10px]' : 'text-[9px]';
    const topMargin = isMobile ? 'mt-1' : 'mt-1';

    // Don't show animations until data is loaded to prevent flashing
    if (!nutritionDataLoaded) {
      return (
        <div className={`w-full ${spacing} ${topMargin} opacity-0`}>
          {/* Placeholder structure */}
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-1">
              <span className={`${textSize} w-3 flex-shrink-0`}>&nbsp;</span>
              <div className={`flex-1 bg-gray-200 dark:bg-gray-800/30 rounded-full ${barHeight}`} />
            </div>
          ))}
        </div>
      );
    }

    // Only animate on initial load, not on subsequent re-renders  
    const shouldAnimate = nutritionDataLoaded && shouldAnimateOnce;

    const progressBars = [
      {
        label: 'Cal',
        progress: progress.calories.progress,
        bgColor: 'bg-green-200 dark:bg-green-800/30',
        barColor: 'bg-gradient-to-r from-green-500 to-green-600',
        textColor: 'text-green-700 dark:text-green-300'
      },
      {
        label: 'Pro',
        progress: progress.protein.progress,
        bgColor: 'bg-blue-200 dark:bg-blue-800/30',
        barColor: 'bg-gradient-to-r from-blue-500 to-blue-600',
        textColor: 'text-blue-700 dark:text-blue-300'
      },
      {
        label: 'Crb',
        progress: progress.carbs.progress,
        bgColor: 'bg-amber-200 dark:bg-amber-800/30',
        barColor: 'bg-gradient-to-r from-amber-500 to-amber-600',
        textColor: 'text-amber-700 dark:text-amber-300'
      },
      {
        label: 'Fat',
        progress: progress.fat.progress,
        bgColor: 'bg-purple-200 dark:bg-purple-800/30',
        barColor: 'bg-gradient-to-r from-purple-500 to-purple-600',
        textColor: 'text-purple-700 dark:text-purple-300'
      }
    ];

    return (
      <div className={`w-full ${spacing} ${topMargin}`}>

        {progressBars.map((bar, index) => (
          <motion.div 
            key={`${date.toDateString()}-${bar.label}`}
            className={`flex items-center ${isMobile ? 'gap-2' : 'gap-1'}`}
            initial={shouldAnimate ? { opacity: 0, x: -8 } : { opacity: 1, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            transition={shouldAnimate ? { 
              duration: 0.08, 
              ease: "easeOut",
              delay: index * 0.01 // Ultra-fast stagger (10ms between bars)
            } : { duration: 0 }}
          >
            <motion.span 
              className={`${textSize} font-medium ${bar.textColor} ${isMobile ? 'w-2.5' : 'w-3'} flex-shrink-0`}
              initial={shouldAnimate ? { opacity: 0 } : { opacity: 1 }}
              animate={{ opacity: 1 }}
              transition={shouldAnimate ? { 
                duration: 0.08, 
                ease: "easeOut",
                delay: index * 0.01 + 0.02 
              } : { duration: 0 }}
            >
              {bar.label}
            </motion.span>
            <div className={`flex-1 ${bar.bgColor} rounded-full ${barHeight} overflow-hidden ${isMobile ? 'min-w-[80px]' : 'min-w-[40px]'}`}>
              <motion.div
                className={`${bar.barColor} rounded-full h-full`}
                style={{ 
                  minWidth: bar.progress > 0 ? '3px' : '0px' // Ensure visible progress even for small values
                }}
                initial={shouldAnimate ? { width: 0 } : { width: `${Math.min(bar.progress, 100)}%` }}
                animate={{ width: `${Math.min(bar.progress, 100)}%` }}
                transition={shouldAnimate ? { 
                  duration: 0.2, 
                  ease: "easeOut",
                  delay: index * 0.015 + 0.05 // Proper delay for bar fill animation
                } : { duration: 0 }}
              />
            </div>
          </motion.div>
        ))}
      </div>
    );
  };
  
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);
      
      // Reset visible meals count when switching between mobile/desktop
      const initialCount = newIsMobile ? MOBILE_INITIAL_MEALS : DESKTOP_INITIAL_MEALS;
      setVisibleMealsCount(initialCount);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Prevent browser native left edge swipe gesture on mobile
  useLeftEdgeSwipeDisable({
    pageId: 'MealTracker',
    enabled: true,
    edgeWidth: 50
  });

  // Update button position when tooltip shows
  useEffect(() => {
    if (showGenerateTooltip && generateButtonRef.current) {
      const updatePosition = () => {
        if (generateButtonRef.current) {
          setButtonRect(generateButtonRef.current.getBoundingClientRect());
        }
      };
      
      updatePosition();
      // Also update on scroll/resize to maintain correct position
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showGenerateTooltip]);

  useEffect(() => {
    // Load meals when component mounts
    loadMeals();
    
    // Note: Removed automatic cleanup on page load to prevent interference with active scans
    // Cleanup now only happens when users explicitly cancel operations via cancelMealScan()
  }, [loadMeals]);

  // Load nutrition data when component mounts or user/week changes
  useEffect(() => {
    loadNutritionData();
  }, [user, currentWeekStart, currentThreeDayStart, isMobile, meals]); // Reload when user data, week/mobile period changes, or meals are added/deleted

  // Scroll detection for frosted glass effect based on direction
  useEffect(() => {
    const scrollContainer = document.querySelector('[data-scroll-container]') as HTMLElement;
    scrollContainerRef.current = scrollContainer;
    
    if (!scrollContainer) return;

    const handleScroll = () => {
      const currentScrollTop = scrollContainer.scrollTop;
      const previousScrollTop = lastScrollTop.current;
      
      // Clear any existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Determine scroll direction with debouncing
      if (currentScrollTop > previousScrollTop && currentScrollTop > 10) {
        // Scrolling down and past initial threshold
        if (scrollDirection.current !== 'down') {
          scrollDirection.current = 'down';
          setIsScrolled(true);
        }
      } else if (currentScrollTop < previousScrollTop) {
        // Scrolling up - immediately return to solid white (no debounce for up)
        if (scrollDirection.current !== 'up') {
          scrollDirection.current = 'up';
          setIsScrolled(false);
        }
      }
      
      // Update last scroll position
      lastScrollTop.current = currentScrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFilterDropdown) {
        const target = event.target as Element;
        const filterButton = target.closest('.relative');
        if (!filterButton) {
          setShowFilterDropdown(false);
        }
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  const handleMealClick = async (mealId: string) => {
    const meal = getMealById(mealId);
    if (meal) {
      // Instead of generating a new signed URL, try to reuse the one already generated by MealImage
      // The MealImage component has already generated a fresh 24-hour signed URL
      console.log('📱 Meal clicked, preparing modal data without duplicate URL generation');
      
      // Store the meal data in sessionStorage so the modal can access it
      const mealAnalysisData = {
        mealData: {
          meal_name: meal.mealName,
          serving_size: meal.servingSize,
          estimated_calories: meal.calories,
          macros: {
            protein: meal.protein,
            carbs: meal.carbs,
            fat: meal.fat
          },
          nutrition: {
            fiber: meal.fiber,
            sugar: meal.sugar,
            sodium: meal.sodium
          },
          ingredients: meal.ingredients,
          confidence: meal.confidence,
          ai_suggestions: meal.aiSuggestions
        },
        imageUrl: meal.imageUrl, // Use the permanent storage path - modal will handle URL generation
        timestamp: new Date().toISOString()
      };
      
      console.log('💾 Storing meal data for modal (no duplicate URL generation):', { 
        mealId, 
        hasImageUrl: !!meal.imageUrl 
      });
      
      sessionStorage.setItem('mealAnalysisData', JSON.stringify(mealAnalysisData));
      setSelectedMealId(mealId);
      setShowNutritionModal(true);
    }
  };

  const handleDeleteMeal = async (mealId: string) => {
    try {
      await deleteMeal(mealId);
      
      // Force refresh nutrition data to update calendar progress bars
      await loadNutritionData();
      
      console.log('✅ Meal deleted and nutrition data refreshed');
    } catch (error) {
      console.error('❌ Error deleting meal:', error);
    }
  };

  const handleRecipeGenerate = async (options: MealGenerationOptions) => {
    console.log('🧠 [MealTracker] Starting AI meal generation with options:', options);
    
    try {
      // Get complete context including user preferences, inventory, and dietary restrictions
      const context = await getCompleteMealGenerationContext();
      
      console.log('📋 [MealTracker] Complete meal generation context loaded:', {
        hasPreferences: !!context.preferences,
        inventoryCount: context.inventory.length,
        userPreferencesCount: context.userPreferences.length,
        userAllergiesCount: context.userAllergies.length
      });
      
      // TODO: Call AI meal generation edge function with complete context
      console.log('✨ [MealTracker] Ready to send to AI with complete context');
      
    } catch (error) {
      console.error('❌ [MealTracker] Failed to prepare meal generation context:', error);
    }
  };

  // Handler for meal scan image using global scanner - EXACT same as Dashboard
  const handleScanImage = async (file: File, scanType: 'meal' = 'meal') => {
    console.log('🔍 [MealTracker] Starting meal scan:', {
      scanType,
      fileName: file.name,
      fileSize: Math.round(file.size / 1024) + 'KB'
    });

    setScanError(null);

    try {
      await startScan(
        file, 
        scanType,
        // On completion callback
        (result) => {
          console.log('✅ [MealTracker] Meal scan completed:', result);
          
          // Check if upgrade is required - now handled globally
          if (result.requiresUpgrade) {
            console.warn('🚫 [MealTracker] Meal scan blocked by usage limits - global modal will show');
            return;
          }
          
          // For meal scanning, show global nutrition analysis modal
          console.log('🍽️ [MealTracker] Meal scan completed - showing global nutrition modal');
          console.log('📱 [MealTracker] Platform detection:', { isMobile, windowWidth: window.innerWidth });
          
          // Use global nutrition analysis modal for both mobile and desktop
          showNutritionAnalysis({
            mealData: result.data,
            imageUrl: result.imageUrl || '',
            imagePath: result.imagePath || '',
            mode: 'analysis'
          });
          
          console.log('🔗 [MealTracker] Image URLs passed to global modal:', {
            imageUrl: result.imageUrl,
            imagePath: result.imagePath
          });
        },
        // On error callback
        (error) => {
          console.error('❌ [MealTracker] Meal scan failed:', error);
          setScanError(error);
        }
      );
    } catch (error: any) {
      console.error('❌ [MealTracker] Failed to start meal scan:', error);
      setScanError(error.message);
    }
  };

  // Trigger meal scan input - EXACT same as Dashboard
  const triggerMealScanInput = () => {
    if (!isMobile && mealScanInputRef.current) {
      mealScanInputRef.current.value = '';
      mealScanInputRef.current.click();
    } else if (isMobile) {
      // For mobile standalone, trigger meal camera directly
      if (mobileMealCameraRef.current) {
        mobileMealCameraRef.current.value = '';
        mobileMealCameraRef.current.click();
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Create timezone-aware date comparisons using local midnight boundaries
    const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days using local dates (eliminates timezone issues)
    const diffTime = todayLocal.getTime() - dateLocal.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays === -1) {
      return 'Tomorrow'; // For edge case where meal might be slightly in future
    } else if (diffDays > 1 && diffDays <= 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < -1) {
      return date.toLocaleDateString();
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className={`max-w-6xl mx-auto ${isMobile ? 'pb-20' : ''}`}>

      {/* Desktop Header - only show on desktop */}
      {!isMobile && (
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Meal Tracker</h1>
            <p className="text-muted-foreground mt-1">View your scanned meals and nutrition history</p>
          </div>
          <div className="flex gap-2">
            {/* <ThemeToggle /> */}
            {/* Fancy AI Generate Button with Tooltip */}
            <div className="relative">
              <Button 
                onClick={() => triggerMealScanInput()}
                disabled={isScanning} // Disable while scanning
                onMouseEnter={() => setShowGenerateTooltip(true)}
                onMouseLeave={() => setShowGenerateTooltip(false)}
                className="relative overflow-hidden bg-gradient-to-r from-purple-500 via-violet-600 to-purple-700 hover:from-purple-600 hover:via-violet-700 hover:to-purple-800 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group ai-scan-button"
                ref={generateButtonRef}
              >
                {/* Animated background shimmer */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                
                {/* Content */}
                                 <div className="relative flex items-center gap-2">
                   <div className="relative">
                     <ScanLine size={18} className="relative z-10" />
                     {/* Sparkle effects */}
                     <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-300 rounded-full ai-sparkle-1" />
                     <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 bg-pink-300 rounded-full ai-sparkle-2" />
                   </div>
                   <span className="font-medium">Meal Scan</span>
                   <Sparkles size={14} className="animate-pulse text-yellow-200" />
                 </div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-md bg-gradient-to-r from-purple-400/20 via-violet-500/20 to-purple-600/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-110" />
              </Button>

              {/* Tooltip */}
              {showGenerateTooltip && buttonRect && ReactDOM.createPortal(
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  style={{
                    position: 'fixed',
                    top: buttonRect.bottom + 8,
                    left: Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)),
                    zIndex: 10000
                  }}
                >
                  {/* Arrow pointing up - positioned relative to button center */}
                  <div 
                    className="w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45 absolute -top-1.5 shadow-sm"
                    style={{
                      left: Math.max(16, Math.min(272, buttonRect.left + (buttonRect.width / 2) - (Math.max(16, Math.min(window.innerWidth - 288, buttonRect.left + (buttonRect.width / 2) - 144)))))
                    }}
                  ></div>
                  
                  {/* Tooltip content */}
                  <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 relative">
                                         <div className="space-y-3">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                           <ScanLine size={12} className="text-white" />
                         </div>
                         <h3 className="font-semibold text-gray-900 text-sm">AI Meal Scanner</h3>
                       </div>
                       
                       <p className="text-gray-600 text-sm leading-relaxed">
                         Scan your meal photo to instantly analyze calories, nutrients, and get detailed nutritional breakdown.
                       </p>
                       
                       <div className="space-y-2">
                         <p className="text-xs font-medium text-gray-700 mb-1">AI will analyze:</p>
                         <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                           <div className="flex items-center gap-1">
                             <span>🔥</span>
                             <span>Calories</span>
                           </div>
                           <div className="flex items-center gap-1">
                             <span>🥩</span>
                             <span>Protein & macros</span>
                           </div>
                           <div className="flex items-center gap-1">
                             <span>🧪</span>
                             <span>Micronutrients</span>
                           </div>
                           <div className="flex items-center gap-1">
                             <span>🍽️</span>
                             <span>Serving size</span>
                           </div>
                         </div>
                       </div>
                     </div>
                  </div>
                </motion.div>,
                document.body
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs for meal scanning - EXACT same as Dashboard */}
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        ref={mealScanInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            console.log('🖥️ [MealTracker Desktop] Meal file selected:', {
              name: file.name,
              size: Math.round(file.size / 1024) + 'KB',
              type: file.type
            });
            handleScanImage(file, 'meal');
          }
        }}
        disabled={isMobile}
      />
      
      {/* Mobile camera input for meal scanning */}
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        capture="environment"
        ref={mobileMealCameraRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            console.log('📱 [MealTracker Mobile] Meal camera file selected:', {
              name: file.name,
              size: Math.round(file.size / 1024) + 'KB',
              type: file.type
            });
            handleScanImage(file, 'meal');
          }
        }}
        disabled={!isMobile}
      />

      {/* Scanning progress indicator */}
      {isScanning && (
        <div className="text-center text-blue-600 font-medium py-2">Analyzing meal...</div>
      )}
      {scanError && (
        <div className="text-center text-red-500 font-medium py-2">{scanError}</div>
      )}

            {/* Mobile Swipeable 3-Day Calendar - Full Width */}
      {isMobile && (
        <div className="w-full">
          {/* Search Bar - Full Width */}
          <div className="px-4 py-3 mb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search meals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-3 rounded-xl bg-gray-50 text-gray-900 placeholder-gray-400 text-base focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Swipeable 3-Day Calendar - Full Width No Padding */}
          <div 
            className="w-full bg-white rounded-xl border border-gray-100 mb-4"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Month/Year Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
              <button
                onClick={goToPrevious3Days}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft size={20} className="text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <h4 className="text-base font-semibold text-gray-900">
                  {currentThreeDayStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h4>
                <button
                  onClick={goToToday}
                  className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full transition-colors"
                >
                  Today
                </button>
              </div>
              <button
                onClick={goToNext3Days}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight size={20} className="text-gray-600" />
              </button>
            </div>
            
            {/* 3-Day Calendar Grid */}
            <div className="grid grid-cols-3 px-2">
              {getThreeDays(currentThreeDayStart).map((date, index) => {
                const isSelected = selectedDate ? selectedDate.toDateString() === date.toDateString() : false;
                const isToday = new Date().toDateString() === date.toDateString();
                const dayMealCount = meals.filter(meal => 
                  new Date(meal.createdAt).toDateString() === date.toDateString()
                ).length;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleDateClick(date)}
                    className={cn(
                      "relative px-3 py-2 transition-all duration-75 min-h-[110px] flex flex-col justify-between border-r border-gray-100 last:border-r-0 rounded-2xl",
                      isSelected 
                        ? "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 border-2 border-orange-300 dark:border-orange-700/50 shadow-sm" 
                        : isToday 
                          ? "bg-blue-50 text-blue-700" 
                          : "hover:bg-gray-50 text-gray-700"
                    )}
                  >
                    {/* Notification-style meal count badge - top right */}
                    {dayMealCount > 0 && (
                      <div className="absolute -top-1 -right-1 z-10">
                        <div className="bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg border-2 border-white">
                          {dayMealCount}
                        </div>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <div className="text-xs uppercase tracking-wide opacity-70 mb-0.5">
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-2xl font-bold mb-0.5">
                        {date.getDate()}
                      </div>
                    </div>
                    
                    {/* Nutrition Progress Bars */}
                    <div className="flex-1 flex flex-col justify-center px-0 py-0">
                      <NutritionProgressBars date={date} isMobile={true} />
                    </div>
                  </button>
                );
              })}
            </div>
            
            {/* Swipe Indicator */}
            <div className="flex justify-center py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-1 bg-gray-300 rounded-full"></div>
                <div className="w-2 h-1 bg-gray-500 rounded-full"></div>
                <div className="w-2 h-1 bg-gray-300 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Search and Date Filter Section */}
      {!isMobile && (
        <div className="sticky top-0 z-40">
          <div className={cn(
            "rounded-xl shadow-soft p-4 transition-all duration-300",
            isScrolled 
              ? cn(
                  "thick-glass-effect",
                  user.floatingBarTheme === 'dark' ? 'floating-bar-dark' : 'floating-bar-light'
                )
              : "bg-white"
          )}>
            {/* Search Bar */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={18} />
              <input
                type="text"
                placeholder="Search meals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  "w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300",
                  isScrolled 
                    ? "bg-white/20 backdrop-blur-sm border-white/30" 
                    : "bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                )}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700 transition-colors"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Date Filters and Week Calendar */}
            <div className="space-y-4">
              {/* Search Context */}
              {searchQuery && (
                <div className="mb-4">
                  <div className="text-sm text-primary">
                    Search results for "{searchQuery}"
                  </div>
                </div>
              )}

              {/* Week Calendar View - Desktop */}
              <div className={cn(
                "rounded-lg px-3 pt-3 pb-0 transition-all duration-300",
                isScrolled 
                  ? "bg-white/10 backdrop-blur-sm border border-white/20" 
                  : "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
              )}>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={goToPreviousWeek}
                    className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ChevronLeft size={16} className="text-gray-600 dark:text-gray-400" />
                  </button>
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {currentWeekStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </h4>
                    <button
                      onClick={goToToday}
                      className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full transition-colors"
                    >
                      Today
                    </button>
                  </div>
                  <button
                    onClick={goToNextWeek}
                    className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ChevronRight size={16} className="text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {getWeekDays(currentWeekStart).map((date, index) => {
                    const isSelected = selectedDate ? selectedDate.toDateString() === date.toDateString() : false;
                    const isToday = new Date().toDateString() === date.toDateString();
                    const dayMealCount = meals.filter(meal => 
                      new Date(meal.createdAt).toDateString() === date.toDateString()
                    ).length;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleDateClick(date)}
                        className={cn(
                          "relative p-2 rounded-lg transition-all duration-200 min-h-[95px] flex flex-col justify-between border-2 border-transparent",
                          isSelected 
                            ? "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 shadow-sm" 
                            : isToday 
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" 
                              : "hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-400"
                        )}
                      >
                        {/* Notification-style meal count badge - top right */}
                        {dayMealCount > 0 && (
                          <div className="absolute -top-1 -right-1 z-10">
                            <div className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg border-2 border-white">
                              {dayMealCount}
                            </div>
                          </div>
                        )}
                        
                        <div className="text-center">
                          <div className="text-[10px] uppercase tracking-wide opacity-70 mb-1">
                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                          </div>
                          <div className="text-base font-bold">
                            {date.getDate()}
                          </div>
                        </div>
                        
                        {/* Nutrition Progress Bars */}
                        <div className="flex-1 flex flex-col justify-center px-1 py-1">
                          <NutritionProgressBars date={date} isMobile={false} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${isMobile ? 'px-1' : ''}`}>
        {meals.length === 0 ? (
          // Empty state for no meals at all
          <div className="bg-card rounded-xl shadow-soft p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
              <Timer size={40} className="text-muted-foreground" />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2 text-foreground">No meals tracked yet</h2>
            <p className="text-muted-foreground mb-6">
              Start scanning your meals from the dashboard to track your nutrition history
            </p>
            <Button 
              onClick={() => window.history.back()}
              className="px-6"
            >
              Go to Dashboard
            </Button>
          </div>
        ) : filteredMeals.length === 0 ? (
          // Empty state for filtered results
          <div className="bg-card rounded-xl shadow-soft p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
              <Search size={40} className="text-muted-foreground" />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2 text-foreground">No meals found</h2>
            <p className="text-muted-foreground mb-6">
              {searchQuery 
                ? `No meals found matching "${searchQuery}" for ${getDateRangeLabel().toLowerCase()}`
                : `No meals found for ${getDateRangeLabel().toLowerCase()}`
              }
            </p>
            <div className="flex gap-3 justify-center">
              {searchQuery && (
                <Button 
                  onClick={clearSearch}
                  variant="outline"
                  className="px-6"
                >
                  Clear Search
                </Button>
              )}
              <Button 
                onClick={() => {
                  setSelectedDateRange('all');
                  setSelectedDate(null);
                }}
                className="px-6"
              >
                Show All Meals
              </Button>
            </div>
          </div>
        ) : (
          // Meals grid
          <div className="space-y-6 mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-heading font-semibold text-foreground">
                {selectedDateRange === 'all' ? 'Your Meals' : `Meals for ${getDateRangeLabel()}`} ({filteredMeals.length})
              </h2>
              {selectedDateRange !== 'all' && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSelectedDateRange('all');
                    setSelectedDate(null);
                  }}
                  className="text-primary hover:text-primary/80"
                >
                  Show All ({meals.length})
                </Button>
              )}
            </div>
            
            <div className={`grid grid-cols-2 lg:grid-cols-3 ${isMobile ? 'gap-1' : 'gap-4'}`}>
              {filteredMeals
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, visibleMealsCount)
                .map((meal) => {
                  
                  // Simplified mobile touch handling to prevent re-renders  
                  const handleMealCardClick = isMobile ? (e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMealClick(meal.id);
                  } : () => handleMealClick(meal.id);

                  // Use simple div for mobile, motion.div for desktop to prevent flashing
                  const CardComponent = isMobile ? 'div' : motion.div;
                  const cardProps = isMobile ? {
                    // Mobile: Simple static card with touch optimization
                    className: `bg-card rounded-xl shadow-soft overflow-hidden cursor-pointer border border-border transition-all duration-75 my-2`,
                    onClick: handleMealCardClick,
                    style: { touchAction: 'manipulation' }
                  } : {
                    // Desktop: Keep animations for desktop
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    whileHover: { scale: 1.02 },
                    whileTap: { scale: 0.98 },
                    transition: { duration: 0.08, ease: "easeOut" },
                    className: `bg-card rounded-xl shadow-soft overflow-hidden cursor-pointer border border-border transition-all duration-75`,
                    onClick: handleMealCardClick
                  };

                  return (
                    <CardComponent
                      key={meal.id}
                      {...cardProps}
                    >
                      {/* Meal Image */}
                      <div className="relative">
                        <CachedMealImage
                          imagePathOrUrl={meal.imageUrl}
                          alt={meal.mealName}
                          className={`w-full object-cover ${isMobile ? 'h-32' : 'h-48'}`}
                        />
                        {/* Only show confidence score for photo_scan meals, not recipe meals */}
                        {meal.source === 'photo_scan' && (
                          <div className="absolute top-3 right-3">
                            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                              {meal.confidence}% confident
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Meal Info */}
                      <div className={isMobile ? 'p-3' : 'p-4'}>
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-heading font-semibold text-foreground text-sm leading-tight">
                            {meal.mealName}
                          </h3>
                        </div>
                        
                        <div className="flex items-center text-xs text-muted-foreground mb-3 gap-2">
                          <Calendar size={12} />
                          <span>{formatDate(meal.createdAt)}</span>
                        </div>
                        
                        {/* Nutrition Summary - Static on mobile to prevent animation flashing */}
                        <div className={`grid grid-cols-3 gap-2 mb-3 ${isMobile ? '' : 'text-center'}`}>
                          {/* Calories - Green */}
                          <div className={`bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-2 ${isMobile ? 'flex flex-col items-center justify-center' : ''}`}>
                            <div className="text-sm font-bold text-green-700 dark:text-green-300">{meal.calories}</div>
                            <div className="text-xs text-green-600 dark:text-green-400">cal</div>
                          </div>
                          {/* Protein - Blue */}
                          <div className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-2 ${isMobile ? 'flex flex-col items-center justify-center' : ''}`}>
                            <div className="text-sm font-bold text-blue-700 dark:text-blue-300">{meal.protein}g</div>
                            <div className="text-xs text-blue-600 dark:text-blue-400">protein</div>
                          </div>
                          {/* Carbs - Yellow/Amber */}
                          <div className={`bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-2 ${isMobile ? 'flex flex-col items-center justify-center' : ''}`}>
                            <div className="text-sm font-bold text-amber-700 dark:text-amber-300">{meal.carbs}g</div>
                            <div className="text-xs text-amber-600 dark:text-amber-400">carbs</div>
                          </div>
                        </div>
                        
                        {/* Ingredients Preview */}
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">Ingredients: </span>
                          <span>
                            {meal.ingredients.slice(0, 3).join(', ')}
                            {meal.ingredients.length > 3 && ` +${meal.ingredients.length - 3} more`}
                          </span>
                        </div>
                      </div>
                    </CardComponent>
                  );
                })}
            </div>
            
            {/* Load More Meals Button */}
            {hasMoreMeals && (
              <div className="flex justify-center mt-6">
                <Button
                  onClick={loadMoreMeals}
                  variant="outline"
                  className="px-6 py-3 rounded-xl border-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                >
                  Load More Meals ({remainingMealsCount} remaining)
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recipe Generation Modal */}
      <MealGenerationModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onGenerate={handleRecipeGenerate}
        inventoryCount={inventory?.length || 0}
        shoppingItemsCount={0} // TODO: Get actual shopping items count from store
      />

      {/* Nutrition Analysis Modal */}
      <NutritionAnalysisModal
        isOpen={showNutritionModal}
        onClose={() => {
          setShowNutritionModal(false);
          setSelectedMealId(null);
        }}
        mode="view"
        mealId={selectedMealId}
        onDeleteMeal={(mealId) => handleDeleteMeal(mealId)}
      />

      {/* Usage Limit Modal now handled globally in App.tsx */}


    </div>
  );
};

export default MealTracker;
